import { Component, OnInit, Input, OnDestroy, EventEmitter, Output } from '@angular/core';
import { FormGroup, FormBuilder, Validators } from "@angular/forms";
import { Subscription } from "rxjs";

import { Module } from "../../../common/models/module.model";
import { NavigationReference } from "../../../common/models/navigationreference.model";
import { ReferenceType } from "../../../common/models/referencetype.enum";
import { OnBehalfSteps } from "../../models/on-behalf.model";

@Component({
    selector: 'on-behalf-application-select',
    templateUrl: './on-behalf-application-select.component.html',
    styleUrls: ['./on-behalf-application-select.component.scss'],
    standalone: false
})
export class OnBehalfApplicationSelectComponent implements OnInit, OnDestroy {

    @Output() applicationChanged: EventEmitter<NavigationReference> = new EventEmitter<NavigationReference>();
    @Input() applicationType: NavigationReference;
    availableApplications: NavigationReference[] = [];
    @Input() availableModules: Module[];
    contactsFlowIds: string[] = ['oxuNg1LoxvMUSQAwU', 'oBvXPWFoBA3A7e2GBM'];
    private _defaultApplication: NavigationReference = { id: "", title: "--Select a Submission Type--", referenceType: ReferenceType.Workflow, showEditWorkflow: false } as NavigationReference;
    private _defaultModule: Module = { id: "", isDefault: false, title: "--Select a Submission--", navigationItems: [] } as Module;

    formGroup: FormGroup = new FormGroup({});
    @Input() module: Module;

    get moduleInvalid(): boolean {
        let result = false;
        if (this.formGroup) {
            let selMod = this.formGroup.get("selectedModule");
            if (selMod) {
                result = selMod.invalid;
            }
        }
        return result;
    }

    @Output() moduleChanged: EventEmitter<Module> = new EventEmitter<Module>();   
    @Output() nextStep: EventEmitter<number> = new EventEmitter<number>();
    subscription: Subscription = new Subscription();
    substitutions: FlowSubstitution[] = [];
    
    get typeInvalid(): boolean {
        let result = false;
        if (this.formGroup) {
            let selApp = this.formGroup.get("selectedApplication");
            if (selApp) {
                result = selApp.invalid;
            }
        }
        return result;
    }

    constructor(private builder: FormBuilder) { }

    ngOnInit() {
        if (this.availableApplications.length === 0 || this.availableApplications.findIndex(a => a.id === this._defaultApplication.id) === -1) {
            this.availableApplications.push(this._defaultApplication);
        }
        this.applicationType = this._defaultApplication;
        this.substitutions = this.substitutionSetup();
        this.buildForm();
    }

    ngOnDestroy() {
        if (this.subscription) this.subscription.unsubscribe();
    }

    advance(): void {
        this.nextStep.emit(OnBehalfSteps.SelectOrganization);
    }

    buildForm(): void {
        this.formGroup = this.builder.group({
            selectedModule: [this.module.id, [Validators.required]],
            selectedApplication: [this.applicationType.id, [Validators.required]]
        });
    }

    cancel(): void {
        this.applicationType = this._defaultApplication;
        this.module = this._defaultModule;
        this.availableApplications = [ this._defaultApplication ];        
        this.formGroup.controls["selectedModule"].patchValue(this.module.id);
        this.formGroup.controls["selectedApplication"].patchValue(this.applicationType.id);
        this.moduleChanged.emit(this.module);
    }

    filterApplications(): void {
        this.availableApplications = [];
        if (this.module.title.toLocaleLowerCase().indexOf("registration") !== -1) {
            this.module.navigationItems.filter(n => n.referenceType === ReferenceType.Workflow && (n.uriComponent === 'nEftdz7nEftgODcOy' || n.showInNavigation || this.contactsFlowIds.includes(n.uriComponent))).forEach(n => {
                this.availableApplications.push(n);
            });
        } else if (this.module.id !== "") {
            this.module.navigationItems.filter(n => n.referenceType === ReferenceType.Workflow && n.showInNavigation).forEach(n => {
                this.availableApplications.push(n);
            });
        }
        this.availableApplications = this.substituteFlows(this.availableApplications);
        this.availableApplications = this.availableApplications.sort((a, b) => {
            if (a.title < b.title) return -1;
            if (b.title < a.title) return 1;
            return 0;
        });
        this.availableApplications.unshift(this._defaultApplication);       
    }

    substituteFlows(reference: NavigationReference[]): NavigationReference[] {
        let consolidated: NavigationReference[] = [];
        for (let r = 0; r < reference.length; r++) {
            const ref = reference[r];
            const replacement = this.substitutions.find(s => s.reference.uriComponent === ref.uriComponent);
            if (replacement != null) {
                if (consolidated.findIndex(c => c.id === replacement.replaceWith.id) === -1) {
                    consolidated.push(replacement.replaceWith);
                }
            } else {
                consolidated.push(ref);
            }            
        }
        return consolidated;
    }

    substitutionSetup(): FlowSubstitution[] {
        let replacements: FlowSubstitution[] = [];
        let subs = new FlowSubstitution();
        subs.reference = {
            uriComponent: "nLF6ry5nLF6M722uxu"
        } as NavigationReference;
        subs.replaceWith = {
            id: "4e14HL",
            uriComponent: "4e14HL",
            title: "Change Owner / Form 7"
        } as NavigationReference;
        replacements.push(subs);
        return replacements;
    }

    validateForm(): void {
        let selMod = this.formGroup.get("selectedModule");
        selMod.updateValueAndValidity();
        if (this.module.id !== selMod.value) {
            this.module = this.availableModules.find(m => m.id === selMod.value);
            this.moduleChanged.emit(this.module);
            this.filterApplications();
            this.formGroup.controls["selectedApplication"].patchValue(this._defaultApplication.id);
        }
        
        let selectedApp = this.formGroup.get("selectedApplication");
        selectedApp.updateValueAndValidity();
        if (this.applicationType.id !== selectedApp.value) {
            this.applicationType = this.availableApplications.find(m => m.id === selectedApp.value);
            this.applicationChanged.emit(this.applicationType);
        }       
    }

}

class FlowSubstitution {
    reference: NavigationReference;
    replaceWith: NavigationReference;
}
